import React, { useState, useMemo, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Divider,
  InputAdornment,
  Box,
} from "@material-ui/core";
import {
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
} from "@material-ui/icons";
import { SiOpenai } from "react-icons/si";
import { NODE_CATEGORIES } from "./nodes";

// Importacoes individuais dos icones (evita import * que carrega ~2100 icones)
import RocketLaunch from "@mui/icons-material/RocketLaunch";
import Webhook from "@mui/icons-material/Webhook";
import Schedule from "@mui/icons-material/Schedule";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import Message from "@mui/icons-material/Message";
import LibraryBooks from "@mui/icons-material/LibraryBooks";
import ImageIcon from "@mui/icons-material/Image";
import MicNone from "@mui/icons-material/MicNone";
import Videocam from "@mui/icons-material/Videocam";
import Description from "@mui/icons-material/Description";
import LocationOn from "@mui/icons-material/LocationOn";
import ContactPhone from "@mui/icons-material/ContactPhone";
import EmojiEmotions from "@mui/icons-material/EmojiEmotions";
import ThumbUp from "@mui/icons-material/ThumbUp";
import DynamicFeed from "@mui/icons-material/DynamicFeed";
import SmartButton from "@mui/icons-material/SmartButton";
import Pix from "@mui/icons-material/Pix";
import ViewList from "@mui/icons-material/ViewList";
import TextFields from "@mui/icons-material/TextFields";
import ImportExport from "@mui/icons-material/ImportExport";
import AccountTree from "@mui/icons-material/AccountTree";
import AltRoute from "@mui/icons-material/AltRoute";
import FilterAlt from "@mui/icons-material/FilterAlt";
import Casino from "@mui/icons-material/Casino";
import AccessTime from "@mui/icons-material/AccessTime";
import HourglassEmpty from "@mui/icons-material/HourglassEmpty";
import CallMerge from "@mui/icons-material/CallMerge";
import Loop from "@mui/icons-material/Loop";
import ViewStream from "@mui/icons-material/ViewStream";
import Cancel from "@mui/icons-material/Cancel";
import ConfirmationNumber from "@mui/icons-material/ConfirmationNumber";
import Label from "@mui/icons-material/Label";
import LabelOff from "@mui/icons-material/LabelOff";
import SupportAgent from "@mui/icons-material/SupportAgent";
import Campaign from "@mui/icons-material/Campaign";
import Http from "@mui/icons-material/Http";
import DataObject from "@mui/icons-material/DataObject";
import Transform from "@mui/icons-material/Transform";
import Code from "@mui/icons-material/Code";
import FindReplace from "@mui/icons-material/FindReplace";
import Email from "@mui/icons-material/Email";
import Psychology from "@mui/icons-material/Psychology";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import SmartToy from "@mui/icons-material/SmartToy";
import Category from "@mui/icons-material/Category";
import StickyNote2 from "@mui/icons-material/StickyNote2";
import PersonSearch from "@mui/icons-material/PersonSearch";
import Receipt from "@mui/icons-material/Receipt";
import LockOpen from "@mui/icons-material/LockOpen";
import Workspaces from "@mui/icons-material/Workspaces";

// Mapa de nomes de icone -> componente (substitui import * as MuiIcons)
const ICON_MAP = {
  RocketLaunch, Webhook, Schedule, ErrorOutline,
  Message, LibraryBooks, Image: ImageIcon, MicNone, Videocam,
  Description, LocationOn, ContactPhone, EmojiEmotions, ThumbUp,
  DynamicFeed, SmartButton, Pix, ViewList, TextFields,
  ImportExport, AccountTree, AltRoute, FilterAlt, Casino,
  AccessTime, HourglassEmpty, CallMerge, Loop, ViewStream, Cancel,
  ConfirmationNumber, Label, LabelOff, SupportAgent, Campaign,
  Http, DataObject, Transform, Code, FindReplace, Email,
  Psychology, AutoAwesome, SmartToy, Category, StickyNote2,
  PersonSearch, Receipt, LockOpen, Workspaces,
};

const EXPANDED_WIDTH = 260;
const COLLAPSED_WIDTH = 48;

/**
 * Mapa de nomes de icones (string) para componentes MUI.
 * O NODE_CATEGORIES usa strings como "RocketLaunch", "AccountTree", etc.
 * Aqui resolvemos para o componente real usando ICON_MAP.
 */
const resolveIcon = (iconName) => {
  if (!iconName) return null;
  return ICON_MAP[iconName] || null;
};

/**
 * Converte NODE_CATEGORIES (de nodes/index.js) para o formato da paleta.
 * Cada categoria ganha um id, e cada node ganha color e description.
 */
const nodeCategories = NODE_CATEGORIES.map((cat, idx) => ({
  id: cat.label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, ""),
  name: cat.label,
  color: cat.color,
  nodes: cat.nodes.map((n) => ({
    type: n.type,
    label: n.label,
    icon: resolveIcon(n.icon),
    iconCustom: n.type === "openai" ? "openai" : undefined,
    color: cat.color,
    description: n.label,
  })),
}));

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    transition: "width 250ms cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden",
    zIndex: 10,
    flexShrink: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 1),
    minHeight: 48,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerTitle: {
    fontSize: "0.85rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    color: theme.palette.text.primary,
  },
  toggleBtn: {
    padding: 6,
  },
  searchField: {
    margin: theme.spacing(1, 1, 0.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: "0.8rem",
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 10px",
    },
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(0.5, 1, 2),
    "&::-webkit-scrollbar": {
      width: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.action.disabled,
      borderRadius: 4,
    },
  },
  categoryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.75, 0.5),
    cursor: "pointer",
    borderRadius: 6,
    userSelect: "none",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  categoryLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  categoryDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  categoryName: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  nodeItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(0.75, 1),
    marginBottom: 2,
    borderRadius: 6,
    cursor: "grab",
    userSelect: "none",
    transition: "background-color 150ms, transform 150ms",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      transform: "translateX(2px)",
    },
    "&:active": {
      cursor: "grabbing",
      opacity: 0.7,
    },
  },
  nodeIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": {
      fontSize: 16,
      color: "#fff",
    },
  },
  nodeLabel: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  collapsedIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: COLLAPSED_WIDTH,
    height: 36,
    cursor: "grab",
    borderRadius: 6,
    margin: "2px 0",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:active": {
      cursor: "grabbing",
      opacity: 0.7,
    },
    "& svg": {
      fontSize: 20,
    },
  },
  divider: {
    margin: theme.spacing(0.5, 0),
  },
  noResults: {
    textAlign: "center",
    padding: theme.spacing(3, 1),
    color: theme.palette.text.disabled,
    fontSize: "0.8rem",
  },
}));

function NodeIcon({ node }) {
  if (node.iconCustom === "openai") {
    return <SiOpenai size={16} color="#fff" />;
  }
  const Icon = node.icon;
  return Icon ? <Icon style={{ fontSize: 16, color: "#fff" }} /> : null;
}

export default function NodePalette({ collapsed, onToggle, onAddNode }) {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const initial = {};
    nodeCategories.forEach((cat) => {
      initial[cat.id] = true;
    });
    return initial;
  });

  const toggleCategory = useCallback((catId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [catId]: !prev[catId],
    }));
  }, []);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return nodeCategories;
    const q = search.toLowerCase().trim();
    return nodeCategories
      .map((cat) => ({
        ...cat,
        nodes: cat.nodes.filter(
          (n) =>
            n.label.toLowerCase().includes(q) ||
            n.type.toLowerCase().includes(q) ||
            n.description.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.nodes.length > 0);
  }, [search]);

  const handleDragStart = useCallback((event, nodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  }, []);

  const handleNodeClick = useCallback(
    (nodeType) => {
      if (onAddNode) {
        onAddNode(nodeType, { x: 400, y: 300 });
      }
    },
    [onAddNode]
  );

  // --- Collapsed view (icon strip) ---
  if (collapsed) {
    return (
      <div className={classes.root} style={{ width: COLLAPSED_WIDTH }}>
        <div className={classes.header} style={{ justifyContent: "center", padding: "6px 4px" }}>
          <IconButton
            className={classes.toggleBtn}
            onClick={onToggle}
            size="small"
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </div>
        <div className={classes.scrollArea} style={{ padding: "4px 6px" }}>
          {nodeCategories.map((cat, catIdx) => (
            <React.Fragment key={cat.id}>
              {catIdx > 0 && (
                <div style={{
                  height: 1, background: "#e0e0e0",
                  margin: "4px 4px",
                }} />
              )}
              {cat.nodes.map((node) => (
                <Tooltip
                  key={node.type}
                  title={node.label}
                  placement="right"
                  arrow
                >
                  <div
                    className={classes.collapsedIcon}
                    draggable
                    onDragStart={(e) => handleDragStart(e, node.type)}
                    onClick={() => handleNodeClick(node.type)}
                    style={{ width: 36, height: 36, margin: "2px auto" }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: 6,
                      backgroundColor: node.color,
                      display: "flex", alignItems: "center",
                      justifyContent: "center",
                    }}>
                      <NodeIcon node={node} />
                    </div>
                  </div>
                </Tooltip>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  // --- Expanded view ---
  return (
    <div className={classes.root} style={{ width: EXPANDED_WIDTH }}>
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>Nodes</Typography>
        <IconButton
          className={classes.toggleBtn}
          onClick={onToggle}
          size="small"
        >
          <ChevronLeft fontSize="small" />
        </IconButton>
      </div>

      <TextField
        className={classes.searchField}
        variant="outlined"
        size="small"
        placeholder="Buscar node..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ fontSize: 18, opacity: 0.5 }} />
            </InputAdornment>
          ),
        }}
      />

      <div className={classes.scrollArea}>
        {filteredCategories.length === 0 && (
          <div className={classes.noResults}>Nenhum node encontrado</div>
        )}

        {filteredCategories.map((category, idx) => (
          <React.Fragment key={category.id}>
            {idx > 0 && <Divider className={classes.divider} />}

            <div
              className={classes.categoryHeader}
              onClick={() => toggleCategory(category.id)}
            >
              <div className={classes.categoryLabel}>
                <span
                  className={classes.categoryDot}
                  style={{ backgroundColor: category.color }}
                />
                <Typography className={classes.categoryName}>
                  {category.name}
                </Typography>
              </div>
              {expandedCategories[category.id] ? (
                <ExpandLess style={{ fontSize: 18, opacity: 0.5 }} />
              ) : (
                <ExpandMore style={{ fontSize: 18, opacity: 0.5 }} />
              )}
            </div>

            <Collapse
              in={expandedCategories[category.id]}
              timeout="auto"
              unmountOnExit
            >
              {category.nodes.map((node) => (
                <Tooltip
                  key={node.type}
                  title={node.description}
                  placement="right"
                  arrow
                  enterDelay={400}
                >
                  <div
                    className={classes.nodeItem}
                    draggable
                    onDragStart={(e) => handleDragStart(e, node.type)}
                    onClick={() => handleNodeClick(node.type)}
                  >
                    <div
                      className={classes.nodeIcon}
                      style={{ backgroundColor: node.color }}
                    >
                      <NodeIcon node={node} />
                    </div>
                    <Typography className={classes.nodeLabel}>
                      {node.label}
                    </Typography>
                  </div>
                </Tooltip>
              ))}
            </Collapse>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
